/**
  * Login modal component for auth challenges
  */

import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface LoginModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    title?: string;
    message?: string;
}

const LoginModal: React.FC<LoginModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    title = 'Sign In Required',
    message = 'Please sign in to continue.',
}) => {
    const { signInWithGoogle, signInWithApple, error: authError } = useAuth();
    const [isSigningIn, setIsSigningIn] = useState(false);
    const [error, setError] = useState<string | null>(null);

    if (!isOpen) return null;

    const handleGoogleSignIn = async () => {
        setIsSigningIn(true);
        setError(null);
        try {
            await signInWithGoogle();
            onSuccess?.();
            onClose();
        } catch (err: any) {
            if (err.code !== 'auth/popup-closed-by-user') {
                setError(err.message || 'Sign-in failed');
            }
        } finally {
            setIsSigningIn(false);
        }
    };

    const handleAppleSignIn = async () => {
        setIsSigningIn(true);
        setError(null);
        try {
            await signInWithApple();
            onSuccess?.();
            onClose();
        } catch (err: any) {
            if (err.code !== 'auth/popup-closed-by-user') {
                setError(err.message || 'Sign-in failed');
            }
        } finally {
            setIsSigningIn(false);
        }
    };

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div style={styles.backdrop} onClick={handleBackdropClick}>
            <div style={styles.modal}>
                <button onClick={onClose} style={styles.closeButton}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                </button>

                <div style={styles.logo}>
                    <div style={styles.logoIcon}>V</div>
                </div>

                <h2 style={styles.title}>{title}</h2>
                <p style={styles.message}>{message}</p>

                {(error || authError) && (
                    <div style={styles.errorBox}>
                        {error || authError}
                    </div>
                )}

                <button
                    onClick={handleGoogleSignIn}
                    disabled={isSigningIn}
                    style={{
                        ...styles.googleButton,
                        opacity: isSigningIn ? 0.7 : 1,
                        cursor: isSigningIn ? 'not-allowed' : 'pointer',
                    }}
                >
                    {isSigningIn ? (
                        <div style={styles.buttonSpinner} />
                    ) : (
                        <svg style={styles.googleIcon} viewBox="0 0 24 24">
                            <path
                                fill="#4285F4"
                                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                            />
                            <path
                                fill="#34A853"
                                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                            />
                            <path
                                fill="#FBBC05"
                                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                            />
                            <path
                                fill="#EA4335"
                                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                            />
                        </svg>
                    )}
                    <span>{isSigningIn ? 'Signing in...' : 'Continue with Google'}</span>
                </button>

                <button
                    onClick={handleAppleSignIn}
                    disabled={isSigningIn}
                    style={{
                        ...styles.appleButton,
                        opacity: isSigningIn ? 0.7 : 1,
                        cursor: isSigningIn ? 'not-allowed' : 'pointer',
                    }}
                >
                    {isSigningIn ? (
                        <div style={styles.buttonSpinner} />
                    ) : (
                        <svg style={styles.appleIcon} viewBox="0 0 24 24">
                            <path
                                fill="#FFFFFF"
                                d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"
                            />
                        </svg>
                    )}
                    <span>{isSigningIn ? 'Signing in...' : 'Continue with Apple'}</span>
                </button>
            </div>
        </div>
    );
};

const styles: { [key: string]: React.CSSProperties } = {
    backdrop: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.4)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '20px',
    },
    modal: {
        background: 'rgba(255, 255, 255, 0.98)',
        borderRadius: '16px',
        padding: '48px',
        maxWidth: '400px',
        width: '100%',
        textAlign: 'center',
        border: '1px solid rgba(0, 0, 0, 0.1)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        position: 'relative',
    },
    closeButton: {
        position: 'absolute',
        top: '16px',
        right: '16px',
        background: 'transparent',
        border: 'none',
        color: '#999999',
        cursor: 'pointer',
        padding: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    logo: {
        marginBottom: '24px',
    },
    logoIcon: {
        width: '56px',
        height: '56px',
        borderRadius: '12px',
        background: '#7C3AED',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#FFFFFF',
        fontWeight: 800,
        fontSize: '28px',
        margin: '0 auto',
    },
    title: {
        fontSize: '24px',
        fontWeight: 700,
        color: '#000000',
        marginBottom: '8px',
    },
    message: {
        fontSize: '14px',
        color: '#666666',
        marginBottom: '24px',
    },
    errorBox: {
        background: 'rgba(239, 68, 68, 0.1)',
        border: '1px solid rgba(239, 68, 68, 0.3)',
        borderRadius: '8px',
        padding: '12px 16px',
        marginBottom: '24px',
        color: '#ef4444',
        fontSize: '14px',
    },
    googleButton: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        background: '#7C3AED',
        color: '#FFFFFF',
        border: 'none',
        borderRadius: '8px',
        padding: '14px 24px',
        fontSize: '16px',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
    },
    googleIcon: {
        width: '20px',
        height: '20px',
    },
    appleButton: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        background: '#000000',
        color: '#FFFFFF',
        border: 'none',
        borderRadius: '8px',
        padding: '14px 24px',
        fontSize: '16px',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        marginTop: '12px',
    },
    appleIcon: {
        width: '20px',
        height: '20px',
    },
    buttonSpinner: {
        width: '20px',
        height: '20px',
        border: '2px solid rgba(255, 255, 255, 0.3)',
        borderTopColor: '#FFFFFF',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
    },
};

export default LoginModal;
